import { AiUsageDimension } from '../enums/aiUsageDimension.enum';
import { AiUsageGridRow, AiUsageRow } from '../models/aiUsageByUser.model';

const ID_SEPARATOR = '/';
const KEY_SEPARATOR = ':';

function rowKey(row: AiUsageRow): string {
  switch (row.rowType) {
    case AiUsageDimension.Org:
      return `org${KEY_SEPARATOR}${row.name ?? ''}`;
    case AiUsageDimension.User:
      return `user${KEY_SEPARATOR}${row.email ?? row.name ?? ''}`;
    default:
      return `month${KEY_SEPARATOR}${row.monthStart ?? ''}`;
  }
}

export function toGridRows(rows: AiUsageRow[], parentId: string | null = null): AiUsageGridRow[] {
  const seen = new Map<string, number>();
  return rows.map(row => {
    const key = rowKey(row);
    const occurrence = seen.get(key) ?? 0;
    seen.set(key, occurrence + 1);
    const uniqueKey = occurrence === 0 ? key : `${key}#${occurrence}`;
    const id = parentId === null ? uniqueKey : `${parentId}${ID_SEPARATOR}${uniqueKey}`;
    return { ...row, id, children: toGridRows(row.children ?? [], id) };
  });
}

function matchesUser(row: AiUsageRow, term: string): boolean {
  return (row.name ?? '').toLowerCase().includes(term) || (row.email ?? '').toLowerCase().includes(term);
}

function sumPrevious(children: AiUsageRow[]): number | null {
  const total = children.reduce((sum, child) => sum + (child.previousCredits ?? 0), 0);
  return total === 0 ? null : total;
}

function regroup(row: AiUsageRow, children: AiUsageRow[]): AiUsageRow {
  return {
    ...row,
    children,
    userCount: children.length,
    questionCount: children.reduce((sum, child) => sum + child.questionCount, 0),
    reportCount: children.reduce((sum, child) => sum + child.reportCount, 0),
    credits: children.reduce((sum, child) => sum + child.credits, 0),
    previousCredits: sumPrevious(children),
  };
}

function filterRow(row: AiUsageRow, term: string): AiUsageRow | null {
  if (row.rowType === AiUsageDimension.User) {
    return matchesUser(row, term) ? row : null;
  }
  const children = row.children.map(child => filterRow(child, term)).filter((child): child is AiUsageRow => child !== null);
  return children.length ? regroup(row, children) : null;
}

export function filterBySearch(rows: AiUsageRow[], search: string): AiUsageRow[] {
  const term = search.trim().toLowerCase();
  if (!term) return rows;
  return rows.map(row => filterRow(row, term)).filter((row): row is AiUsageRow => row !== null);
}
